import { CHATBOT_INTENTS, UNKNOWN_INTENT } from "./chatbot-intents.config";
import { ChatAction, ChatbotQueryFilter, ContextKind, Intent, ScoredIntent } from "./chatbot.types";
import { DateRange } from "../../interfaces/DatesGlobalInterface.type";

/** Runner-up within this many points of the best intent counts as a tie. */
const TIE_MARGIN = 2;
/** Tiebreak favouring sub-sections (navigate-a-b) over their parent section (navigate-a). */
const DEPTH_BONUS = 5;
/** Minimum NLP confidence to promote a non-navigation intent over regex. */
const NLP_SIMPLE_MIN_SCORE = 0.6;
const NLP_NAV_MIN_SCORE = 0.5;
/** NLP weight: confirming a regex match vs proposing an intent regex missed. */
const NLP_AGREE_WEIGHT = 10;
const NLP_ONLY_WEIGHT = 20;

const QUERY_SCREEN = "analisis-datos/consultas";
const ANALYSIS_PREFIX = "analisis-datos";
const DEVICE_LIST_PREFIX = "dispositivos/listado";

/** List screen of each context kind: "cliente Suez" switches client instead of opening the list. */
const CONTEXT_LIST_PAYLOAD: Readonly<Record<ContextKind, string>> = {
  client: "clientes/listado",
  entity: "entidades/listado",
  agrupation: "agrupaciones/listado"
};
const RE_LIST_REQUEST = /\b(?:listado|lista|list|liste|todos|todas|all)\b/;

/** Signals extracted from the message that bias intent ranking. */
export interface IntentSignals {
  dateRange: DateRange | null;
  queryFilter: ChatbotQueryFilter | null;
  /** True when a client, entity, or agrupation term was extracted from the message. */
  hasContextTerm: boolean;
}

/** NLP classification result and its confidence score. */
export interface NlpMatch {
  intent: Intent | null;
  score: number;
}

/** Outcome of intent ranking: navigation target, optional tie, or fallback intent. */
export interface IntentDecision {
  /** Best navigation intent, or null when nothing navigates. */
  navIntent: Intent | null;
  /** Set when the runner-up ties with navIntent and no signal breaks the tie. */
  tiedWith: Intent | null;
  /** Non-navigation intent (greeting, help, context switch...) used when navIntent is null. */
  simpleIntent: Intent;
}

/** Specificity of a pattern = length of the text it explains. */
function matchedLength(pattern: RegExp, normalized: string): number {
  return pattern.exec(normalized)?.[0].trim().length ?? 0;
}

/** Number of ancestor intents (ids that prefix this id), e.g. navigate-dispositivos-mbus → 1. */
function intentDepth(intent: Intent, intents: readonly Intent[]): number {
  return intents.filter(o => intent.id.startsWith(o.id + "-")).length;
}

/**
 * Regex scan: every matching NAVIGATE intent scored by its longest match,
 * plus the first matching non-navigation intent.
 */
export function collectMatchedIntents(
  normalized: string,
  intents: readonly Intent[] = CHATBOT_INTENTS
): { navIntents: ScoredIntent[]; primaryIntent: Intent | null } {
  const navIntents: ScoredIntent[] = [];
  let primaryIntent: Intent | null = null;

  for (const intent of intents) {
    const length = Math.max(0, ...intent.patterns.map(p => matchedLength(p, normalized)));
    if (length === 0) {
      continue;
    }
    if (intent.action === ChatAction.NAVIGATE && intent.payload) {
      navIntents.push({ intent, score: length + intentDepth(intent, intents) * DEPTH_BONUS });
    } else if (!primaryIntent && intent.action !== ChatAction.NAVIGATE) {
      primaryIntent = intent;
    }
  }
  if (primaryIntent?.contextKind && !RE_LIST_REQUEST.test(normalized)) {
    const listPayload = CONTEXT_LIST_PAYLOAD[primaryIntent.contextKind];
    return { navIntents: navIntents.filter(s => s.intent.payload !== listPayload), primaryIntent };
  }
  return { navIntents, primaryIntent };
}

/** Adjusts scores for query and date signals to favor compatible screens and penalize plain device lists. */
function signalBonus(intent: Intent, signals: IntentSignals): number {
  const payload = intent.payload ?? "";
  let bonus = 0;
  if (signals.queryFilter && payload === QUERY_SCREEN) {
    bonus += 40;
  }
  if (signals.dateRange && payload.startsWith(ANALYSIS_PREFIX)) {
    bonus += 15;
  }
  // A plain device list can't show a query or a period; filtered lists (tableFilter) can.
  if ((signals.queryFilter || signals.dateRange) && payload.startsWith(DEVICE_LIST_PREFIX) && !intent.tableFilter) {
    bonus -= 50;
  }
  return bonus;
}

/** Ranks regex candidates with signal bonuses and merges the NLP classification. */
export function rankNavIntents(
  navIntents: readonly ScoredIntent[],
  signals: IntentSignals,
  nlp: NlpMatch,
  allowNlpOnly = true
): ScoredIntent[] {
  const ranked = navIntents.map(si => ({ intent: si.intent, score: si.score + signalBonus(si.intent, signals) }));

  if (nlp.intent?.action === ChatAction.NAVIGATE && nlp.score >= NLP_NAV_MIN_SCORE) {
    const existing = ranked.find(s => s.intent.id === nlp.intent.id);
    if (existing) {
      existing.score += Math.round(nlp.score * NLP_AGREE_WEIGHT);
    } else if (allowNlpOnly) {
      ranked.push({
        intent: nlp.intent,
        score: Math.round(nlp.score * NLP_ONLY_WEIGHT) + signalBonus(nlp.intent, signals)
      });
    }
  }
  return ranked.sort((a, b) => b.score - a.score);
}

/** Full decision: best navigation intent (or tie), else the non-navigation fallback. */
export function decideIntent(
  matches: { navIntents: readonly ScoredIntent[]; primaryIntent: Intent | null },
  signals: IntentSignals,
  nlp: NlpMatch = { intent: null, score: 0 }
): IntentDecision {
  // An explicit context switch ("entidad Aqualia") only navigates where regex says so.
  const isContextSwitch = !!matches.primaryIntent?.contextKind;
  const [best, runnerUp] = rankNavIntents(matches.navIntents, signals, nlp, !isContextSwitch);

  const hasStrongSignal = !!(signals.queryFilter || signals.dateRange || signals.hasContextTerm);
  const tied =
    !!best &&
    !!runnerUp &&
    !hasStrongSignal &&
    best.score - runnerUp.score <= TIE_MARGIN &&
    best.intent.payload !== runnerUp.intent.payload;

  const useNlpSimple =
    !!nlp.intent && nlp.intent.action !== ChatAction.NAVIGATE && nlp.score >= NLP_SIMPLE_MIN_SCORE;

  return {
    navIntent: best?.intent ?? null,
    tiedWith: tied ? runnerUp.intent : null,
    simpleIntent: useNlpSimple ? nlp.intent : (matches.primaryIntent ?? UNKNOWN_INTENT)
  };
}
